import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../api/services/authService';
import { shouldProbeSessionOnLoad, setSessionActive } from '../utils/authRouteUtils';

const AuthContext = createContext();

const clearLegacyAuthStorage = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('token');
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('token');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        clearLegacyAuthStorage();

        if (!shouldProbeSessionOnLoad()) {
          setUser(null);
          setIsAuthenticated(false);
          return;
        }

        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setSessionActive();
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await authService.login(email, password, rememberMe);
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        return { success: true };
      }

      return {
        success: false,
        error: response?.error || 'Login failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Network error. Please check your connection.'
      };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // ignore
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      clearLegacyAuthStorage();
    }
  };

  const getCurrentUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true, data: userData };
      }

      setUser(null);
      setIsAuthenticated(false);
      return { success: false, error: 'No user data found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const loginWithOTP = async (userData) => {
    try {
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true };
      }

      return { success: false, error: 'No user data provided' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const verifyOTP = async (email, otp) => {
    try {
      const response = await authService.verifyOTP({ emailOrPhone: email, otp });
      return response;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithOTP,
    logout,
    verifyOTP,
    getCurrentUser,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
